import { ensurePropOf } from '@farol-ds/react'
import { assignSubComponents } from '@farol-ds/react/utilities/internal'
import classNames from 'classnames'
import { forwardRef, HTMLAttributes, ReactNode } from 'react'

import { PriceAlign, PriceAligns, PriceSize, PriceSizes } from './constants'
import { PriceContext } from './context'
import styles from './price.module.scss'
import PriceDiscount from './price-discount'
import PriceDiscountStrike from './price-discount-strike'
import PriceHelptext from './price-helptext'
import PriceTitle from './price-title'
import PriceValue from './price-value'
import PriceValueFrequency from './price-value-frequency'

export type PriceElement = HTMLDivElement

export interface PriceProps extends HTMLAttributes<PriceElement> {
  children?: ReactNode
  className?: string
  size?: PriceSize
  align?: PriceAlign
}

const PriceRoot = forwardRef<PriceElement, PriceProps>(function Price(props, forwardedRef) {
  const { className, children, size: sizeProp, align: alignProp, ...rest } = props

  const size = ensurePropOf<PriceSize>(PriceSizes, sizeProp, 'md')
  const align = ensurePropOf<PriceAlign>(PriceAligns, alignProp, 'left')

  const classes = classNames(
    styles.root,
    {
      [styles[`size${size}`]]: !!size,
      [styles[`align${align}`]]: !!align,
    },
    className
  )

  return (
    <PriceContext.Provider value={{ size, align }}>
      <div {...rest} className={classes} ref={forwardedRef}>
        {children}
      </div>
    </PriceContext.Provider>
  )
})

export const Price = assignSubComponents('Price', PriceRoot, {
  Discount: PriceDiscount,
  DiscountStrike: PriceDiscountStrike,
  Helptext: PriceHelptext,
  Title: PriceTitle,
  Value: PriceValue,
  ValueFrequency: PriceValueFrequency,
})

export * from './constants'
export * from './price-discount'
export * from './price-discount-strike'
export * from './price-helptext'
export * from './price-title'
export * from './price-value'
export * from './price-value-frequency'
